import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { projectsAPI } from '../../services/api';
import {
  ACTIVITY_WINDOW_LABEL,
  ACTIVITY_CARD_BODY_CLASS,
  ActivityStat,
  PeriodSelect,
} from '../activity/ActivityCardShared';
import AvatarGroup from '../ui/AvatarGroup';
import {
  FolderKanban,
  GitFork,
  Users,
  GitCommit,
  GitPullRequest,
  ChevronRight,
} from 'lucide-react';

/**
 * Organisation-level project summary: loads card-stats per selected period on this card.
 *
 * @param {{ orgId: string, streamId: string, project: { id: string, name?: string }, streamLabel?: string }} props
 */
export default function OrganisationProjectCard({ orgId, streamId, project, streamLabel }: any) {
  const [timeframe, setTimeframe] = useState('month');
  const [row, setRow] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [members, setMembers] = useState<any[]>([]);

  const projectId = project?.id;

  useEffect(() => {
    if (!orgId || !streamId || !projectId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    projectsAPI
      .getCardStats(orgId, streamId, projectId, { timeframe })
      .then((card) => {
        if (!cancelled) setRow(card);
      })
      .catch(() => {
        if (!cancelled) {
          setRow(null);
          setLoadError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, streamId, projectId, timeframe]);

  useEffect(() => {
    const memberCount = row?.memberCount ?? 0;
    if (!orgId || !streamId || !projectId || memberCount === 0) {
      setMembers([]);
      return undefined;
    }
    let cancelled = false;
    projectsAPI
      .getMembers(orgId, streamId, projectId)
      .then((list) => {
        if (!cancelled) setMembers(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, streamId, projectId, row?.memberCount]);

  const display = row;
  const tf = timeframe === 'week' || timeframe === 'year' ? timeframe : 'month';
  const windowLabel = ACTIVITY_WINDOW_LABEL[tf];
  const repos = Array.isArray(display?.repositories) ? display.repositories : [];
  const name = display?.name ?? project?.name ?? 'Project';
  const commitCount = display?.commitCount ?? 0;
  const activeContributorCount = display?.activeContributorCount ?? 0;
  const mergedPullRequestCount = display?.mergedPullRequestCount ?? 0;

  return (
    <article className="card-mdl flex flex-col h-full min-h-[260px] overflow-hidden">
      <div className="px-5 pt-5 pb-4 flex items-center gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-vivid-purple/20 flex items-center justify-center ring-1 ring-vivid-purple/25">
          <FolderKanban className="w-5 h-5 text-vivid-purple" aria-hidden />
        </div>
        <div className="min-w-0 flex-1 flex flex-col gap-0.5">
          <div className="flex min-w-0 items-center gap-2">
            <h3
              className="font-semibold text-slate-100 truncate text-base leading-snug"
              title={name}
            >
              {name}
            </h3>
            {members.length > 0 ? <AvatarGroup members={members} max={4} size="xs" /> : null}
          </div>
          {streamLabel ? <p className="text-sm text-slate-400 truncate">in {streamLabel}</p> : null}
        </div>
        <Link
          to={`/streams/${streamId}/projects/${projectId}`}
          className="btn-icon-mdl btn-icon-mdl-sm flex-shrink-0"
          aria-label={`Open project ${name}`}
          title={`Open project ${name}`}
        >
          <ChevronRight className="w-5 h-5" aria-hidden />
        </Link>
      </div>

      <div className={ACTIVITY_CARD_BODY_CLASS}>
        {loadError && <p className="text-amber-300">Could not load activity for this project.</p>}

        <div>
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              Activity
            </p>
            <PeriodSelect
              id={`project-card-timeframe-${projectId}`}
              value={timeframe}
              onChange={setTimeframe}
              disabled={loading}
            />
          </div>
          <div
            className={`grid grid-cols-3 gap-2 ${loading ? 'opacity-60 pointer-events-none' : ''}`}
            aria-busy={loading}
          >
            <ActivityStat
              icon={GitCommit}
              iconClass="text-vivid-cyan-blue"
              value={commitCount}
              label="Commits"
              title={`Commits (${windowLabel})`}
            />
            <ActivityStat
              icon={Users}
              iconClass="text-vivid-green-cyan"
              value={activeContributorCount}
              label="Contributors"
              title={`Active contributors (${windowLabel})`}
            />
            <ActivityStat
              icon={GitPullRequest}
              iconClass="text-luminous-vivid-amber"
              value={mergedPullRequestCount}
              label="Merged PRs"
              title={`Merged pull requests (${windowLabel})`}
            />
          </div>
        </div>

        <div className="flex flex-col min-h-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
            Repositories
          </p>
          {loading && !display ? (
            <p className="text-sm text-slate-400">Loading repositories…</p>
          ) : repos.length === 0 ? (
            <p className="text-sm text-slate-400">No repositories in this project yet.</p>
          ) : (
            <ul className="rounded-xl border border-dark-border divide-dark-elevated overflow-hidden bg-dark-surface/40/30">
              {repos.map((r) => (
                <li key={r.id}>
                  <Link
                    to={`/repositories/${r.id}`}
                    className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm text-slate-100 hover:bg-dark-elevated/80 transition-colors group"
                  >
                    <span className="font-medium truncate min-w-0 group-hover:text-vivid-red transition-colors">
                      {r.fullName || r.name || r.id}
                    </span>
                    <GitFork
                      className="w-3.5 h-3.5 text-slate-400 opacity-70 shrink-0"
                      aria-hidden
                    />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </article>
  );
}
